import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.authToken = null;
    this.messageHandlers = new Map();
    this.typingHandlers = new Map();
  }

  // Initialize socket connection
  connect(token) {
    if (this.socket && this.isConnected) {
      return Promise.resolve();
    }

    this.authToken = token;
    
    // Get the base URL from the API service
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const socketURL = baseURL.replace('/api', '');

    this.socket = io(socketURL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      auth: {
        token: token
      }
    });

    return new Promise((resolve, reject) => {
      this.socket.on('connect', () => {
        console.log('🔌 Socket connected');
        this.isConnected = true;
        resolve();
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        reject(error);
      });

      this.socket.on('disconnect', () => {
        console.log('🔌 Socket disconnected');
        this.isConnected = false;
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        reject(error);
      });
    });
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.authToken = null;
    }
  }

  // Join a chat room
  joinChat(chatId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join_chat', chatId);
      console.log(`👥 Joined chat: ${chatId}`);
    }
  }

  // Leave a chat room
  leaveChat(chatId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave_chat', chatId);
      console.log(`👋 Left chat: ${chatId}`);
    }
  }

  // Send a message
  sendMessage(chatId, content, type = 'text') {
    if (this.socket && this.isConnected) {
      this.socket.emit('send_message', { chatId, content, type });
      console.log(`💬 Sending message to chat: ${chatId}`);
    } else {
      throw new Error('Socket not connected');
    }
  }

  // Start typing indicator
  startTyping(chatId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('start_typing', chatId);
    }
  }

  // Stop typing indicator
  stopTyping(chatId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('stop_typing', chatId);
    }
  }

  // Listen for new messages
  onNewMessage(chatId, handler) {
    const key = `new_message_${chatId}`;
    
    // Remove any existing handler for this chat
    const existingHandler = this.messageHandlers.get(key);
    if (existingHandler) {
      this.socket.off('new_message', existingHandler);
    }
    
    this.messageHandlers.set(key, handler);
    
    this.socket.on('new_message', (message) => {
      console.log(`📨 Received new_message for chat ${chatId}:`, message);
      // Since the server emits to specific chat rooms, we can trust this message is for us
      handler(message);
    });
  }

  // Listen for typing indicators
  onUserTyping(chatId, handler) {
    const key = `typing_${chatId}`;
    this.typingHandlers.set(key, handler);
    
    this.socket.on('user_typing', (data) => {
      console.log(`⌨️ User typing in chat ${chatId}:`, data);
      if (data.chatId === chatId) {
        handler(data);
      }
    });

    this.socket.on('user_stop_typing', (data) => {
      console.log(`⌨️ User stopped typing in chat ${chatId}:`, data);
      if (data.chatId === chatId) {
        handler({ ...data, isTyping: false });
      }
    });
  }

  // Remove message handler
  offNewMessage(chatId) {
    const key = `new_message_${chatId}`;
    const handler = this.messageHandlers.get(key);
    if (handler) {
      this.socket.off('new_message', handler);
      this.messageHandlers.delete(key);
    }
  }

  // Remove typing handler
  offUserTyping(chatId) {
    const key = `typing_${chatId}`;
    const handler = this.typingHandlers.get(key);
    if (handler) {
      this.socket.off('user_typing', handler);
      this.socket.off('user_stop_typing', handler);
      this.typingHandlers.delete(key);
    }
  }

  // Helper method to check if message is for a specific chat
  isMessageForChat(message, chatId) {
    // This is a fallback check - the server should handle this properly
    return true;
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket ? this.socket.id : null
    };
  }

  // Check if socket is connected
  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected;
  }
}

export default new SocketService(); 